<template>
  <div class="flex w-full h-screen overflow-hidden bg-slate-100">
    <div class="flex-1 w-full overflow-hidden">
      <Topbar />

      <div class="mt-[64px] flex h-[calc(100vh-64px)] flex-col overflow-hidden p-4 md:p-5 lg:p-6">
        <Toast />

        <div class="mb-4 flex items-start justify-between gap-3 shrink-0">
          <div>
            <h2 class="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">Клиенты</h2>
            <p class="mt-1 text-sm text-slate-500 md:text-base">База клиентов магазина</p>
          </div>
          <div class="flex flex-wrap items-center justify-end gap-2">
            <div class="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-slate-600">
              Клиентов <span class="ml-1 font-semibold text-emerald-700">{{ formattedClientCount }}</span>
            </div>
            <input
              ref="importFileInput"
              type="file"
              accept=".xlsx,.xls"
              class="hidden"
              @change="handleImportFile"
            >
            <Button
              icon="pi pi-upload"
              label="Импорт Excel"
              severity="info"
              class="!px-3 !py-2 !text-sm !shadow-sm"
              :loading="importInProgress"
              @click="openImportPicker"
            />
            <Button
              icon="pi pi-download"
              label="Экспорт Excel"
              severity="secondary"
              class="!px-3 !py-2 !text-sm !shadow-sm"
              @click="exportClients"
            />
            <Button
              icon="pi pi-calendar"
              label="Напоминания"
              severity="contrast"
              class="!px-3 !py-2 !text-sm !shadow-sm"
              @click="router.push({ name: 'ClientReminders' })"
            />
            <Button
              icon="pi pi-plus"
              label="Добавить клиента"
              severity="success"
              class="!px-3 !py-2 !text-sm !shadow-sm"
              @click="addClientDialog = true"
            />
          </div>
        </div>

        <div class="min-h-0 flex flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-6">
          <div class="mb-4 flex shrink-0 items-center justify-between gap-3">
            <div class="flex max-w-4xl flex-1 flex-wrap gap-2">
              <IconField class="min-w-[16rem] flex-1">
                <InputIcon><i class="pi pi-search" /></InputIcon>
                <InputText
                  v-model.trim="searchQuery"
                  class="w-full"
                  placeholder="Поиск клиента по имени или телефону..."
                />
              </IconField>

              <Select
                v-model="selectedClientTypeFilter"
                :options="clientTypeFilterOptions"
                optionLabel="label"
                optionValue="value"
                class="min-w-[14rem]"
              />

              <InputText
                v-model="createdAtFilter.from"
                type="date"
                class="min-w-[11rem]"
              />

              <InputText
                v-model="createdAtFilter.to"
                type="date"
                class="min-w-[11rem]"
              />
            </div>


            <div class="flex flex-wrap items-center justify-end gap-2">
              <div class="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-1">
                <Button
                  label="Активные"
                  size="small"
                  :severity="clientScope === 'active' ? 'success' : 'secondary'"
                  :outlined="clientScope !== 'active'"
                  @click="setClientScope('active')"
                />
                <Button
                  label="Удалённые"
                  size="small"
                  :severity="clientScope === 'deleted' ? 'success' : 'secondary'"
                  :outlined="clientScope !== 'deleted'"
                  @click="setClientScope('deleted')"
                />
              </div>

              <div class="flex items-center gap-2">
              <Button
                label="Сетка"
                icon="pi pi-th-large"
                size="small"
                :severity="viewMode === 'grid' ? 'success' : 'secondary'"
                :outlined="viewMode !== 'grid'"
                @click="viewMode = 'grid'"
              />
              <Button
                label="Таблица"
                icon="pi pi-table"
                size="small"
                :severity="viewMode === 'table' ? 'success' : 'secondary'"
                :outlined="viewMode !== 'table'"
                @click="viewMode = 'table'"
              />
              </div>
            </div>
          </div>

          <div v-if="!filteredClients.length" class="min-h-0 flex-1 overflow-y-auto pr-1">
            <div class="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center text-slate-500">
              Клиенты не найдены
            </div>
          </div>

          <div v-else-if="viewMode === 'grid'" class="min-h-0 flex-1 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 p-4 pr-1">
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
              <article
                v-for="item in filteredClients"
                :key="item.id"
                class="cursor-pointer rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-colors hover:border-emerald-200 hover:bg-emerald-50/30"
                @click="openDetails(item)"
              >
                <div class="flex items-start justify-between gap-3">
                  <div class="flex min-w-0 items-start gap-3">
                    <div
                      class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-base font-semibold"
                      :style="getClientInitialStyle(item.client_type_id)"
                    >
                      {{ initialFromName(item.name) }}
                    </div>
                    <div class="min-w-0 ml-3">
                      <p class="truncate text-base font-semibold text-slate-900 md:text-lg">{{ item.name || 'Без имени' }}</p>
                      <p class="truncate text-sm text-slate-500">
                        <i class="pi pi-phone mr-1" />
                        {{ formatPhone(item.phone) || 'Телефон не указан' }}
                      </p>
                      <p class="truncate text-sm text-slate-500">
                        <i class="pi pi-envelope mr-1" />
                        {{ item.email || 'Email не указан' }}
                      </p>
                      <p v-if="item.description" class="mt-1 line-clamp-2 text-sm text-slate-500">
                        {{ item.description }}
                      </p>
                      <p class="mt-1 text-sm text-slate-500">
                        Покупки: <span class="font-semibold text-slate-900">{{ formatCurrency(item.total_price) }}</span>
                      </p>
                      <p class="mt-1 text-xs text-slate-400">
                        Создан: {{ formatDate(item.created_at) }}
                      </p>
                      <p v-if="item.deleted_at" class="mt-1 text-xs font-medium text-rose-600">
                        Удалён
                      </p>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </div>

          <div v-else class="min-h-0 flex-1 overflow-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div class="min-h-full min-w-max">
                <table class="min-w-full divide-y divide-slate-200 text-sm">
                  <thead class="bg-slate-50">
                    <tr>
                      <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">Клиент</th>
                      <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">Телефон</th>
                      <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">Email</th>
                      <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">Описание</th>
                      <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">Пол</th>
                      <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">
                        <button type="button" class="flex items-center gap-2 transition-colors hover:text-slate-900" @click="toggleSort('created_at')">
                          <span>Создан</span>
                          <i :class="getSortIconClass('created_at')"></i>
                        </button>
                      </th>
                      <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">
                        <button type="button" class="flex items-center gap-2 transition-colors hover:text-slate-900" @click="toggleSort('total_amount')">
                          <span>Заказов</span>
                          <i :class="getSortIconClass('total_amount')"></i>
                        </button>
                      </th>
                      <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">
                        <button type="button" class="flex items-center gap-2 transition-colors hover:text-slate-900" @click="toggleSort('total_price')">
                          <span>Покупки</span>
                          <i :class="getSortIconClass('total_price')"></i>
                        </button>
                      </th>
                      <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">
                        <button type="button" class="flex items-center gap-2 transition-colors hover:text-slate-900" @click="toggleSort('average_price')">
                          <span>Средний чек</span>
                          <i :class="getSortIconClass('average_price')"></i>
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-100 bg-white">
                    <tr
                      v-for="item in filteredClients"
                      :key="item.id"
                      class="cursor-pointer transition-colors hover:bg-emerald-50/50"
                      @click="openDetails(item)"
                    >
                      <td class="px-4 py-3">
                        <div class="flex min-w-0 items-center gap-3">
                          <div
                            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
                            :style="getClientInitialStyle(item.client_type_id)"
                          >
                            {{ initialFromName(item.name) }}
                          </div>
                          <div class="min-w-0">
                            <p class="truncate font-semibold text-gray-900">{{ item.name || 'Без имени' }}</p>
                            <p class="truncate text-xs text-gray-500">{{ getClientTypeLabel(item.client_type) }}</p>
                          </div>
                        </div>
                      </td>
                      <td class="px-4 py-3 text-gray-600">{{ formatPhone(item.phone) || 'Телефон не указан' }}</td>
                      <td class="px-4 py-3 text-gray-600">{{ item.email || 'Email не указан' }}</td>
                      <td class="max-w-[20rem] px-4 py-3 text-gray-600">
                        <p class="line-clamp-2">{{ item.description || '—' }}</p>
                      </td>
                      <td class="px-4 py-3 text-gray-600">{{ item.sex || '—' }}</td>
                      <td class="px-4 py-3 text-gray-600">{{ formatDate(item.created_at) }}</td>
                      <td class="px-4 py-3 text-gray-600">{{ Number(item.total_amount || 0) }}</td>
                      <td class="px-4 py-3">
                        <p class="font-semibold text-gray-900">{{ formatCurrency(item.total_price) }}</p>
                        <p v-if="item.deleted_at" class="mt-1 text-xs font-medium text-rose-600">Удалён</p>
                      </td>
                      <td class="px-4 py-3 text-gray-600">{{ formatCurrency(item.average_price) }}</td>
                    </tr>
                  </tbody>
                </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <Dialog v-model:visible="addClientDialog" :style="{ width: '600px' }" header="Добавить клиента" :modal="true">
    <div class="grid grid-cols-12 justify-between">
      <div class="flex items-center col-span-12 gap-2 w-full">
        <FloatLabel variant="on" class="col-span-6 mt-2 w-1/2">
          <label for="name" class="block z-10">ФИО</label>
          <InputText id="name" v-model.trim="client.name" required fluid class="w-full" />
        </FloatLabel>
        <FloatLabel variant="on" class="col-span-6 mt-2 w-1/2">
          <label for="phone" class="block z-10">Телефон</label>
          <InputMask
            id="phone"
            v-model.trim="client.phone"
            required
            fluid
            mask="+7(799)999-99-99"
            placeholder="+7(799)999-99-99"
            class="w-full"
          />
        </FloatLabel>
      </div>

      <div class="flex items-center col-span-12 gap-2 mb-2">
        <FloatLabel variant="on" class="col-span-6 mt-2">
          <label for="email" class="block z-10">Email</label>
          <InputText id="email" v-model.trim="client.email" fluid />
        </FloatLabel>
        <FloatLabel variant="on" class="col-span-6 mt-2">
          <label for="sex" class="block z-10">Пол</label>
          <Select v-model="client.sex" showClear :options="sex" filter optionLabel="name" fluid required />
        </FloatLabel>
      </div>

      <div class="col-span-12 mb-2">
        <FloatLabel variant="on" class="mt-2 block">
          <label for="description" class="block z-10">Описание</label>
          <Textarea id="description" v-model.trim="client.description" fluid rows="3" class="w-full" />
        </FloatLabel>
      </div>

      <div class="col-span-12">
        <FloatLabel variant="on" class="mt-2 block">
          <label for="client-type" class="block z-10">Тип клиента</label>
          <Select
            id="client-type"
            v-model="client.client_type_id"
            :options="clientTypes"
            optionLabel="name"
            optionValue="id"
            placeholder="Выберите тип клиента"
            class="w-full"
            fluid
          />
        </FloatLabel>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-evenly w-full gap-2">
        <Button label="Отменить" icon="pi pi-times" @click="addClientDialog = false" severity="secondary" fluid />
        <Button label="Добавить" icon="pi pi-check" @click="addClient()" severity="success" fluid />
      </div>
    </template>
  </Dialog>

  <Dialog
    v-model:visible="showDetailsDialog"
    :style="{ width: '1080px', maxWidth: '96vw' }"
    header="Карточка клиента"
    :modal="true"
    @hide="handleClientDetailsHide"
  >
    <div v-if="selectedClientDetails" class="space-y-4">
      <section
        class="rounded-2xl border p-5"
        :style="getClientHeroStyle(selectedClientDetails.client_type_id)"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="flex min-w-0 items-start gap-4">
            <div
              class="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-xl font-semibold"
              :style="getClientInitialStyle(selectedClientDetails.client_type_id)"
            >
              {{ initialFromName(selectedClientDetails.name) }}
            </div>
            <div class="min-w-0">
              <p class="truncate text-xl font-semibold text-slate-900">{{ selectedClientDetails.name || 'Без имени' }}</p>
              <p class="mt-1 text-sm text-slate-500">{{ formatPhone(selectedClientDetails.phone) || 'Телефон не указан' }}</p>
              <div class="mt-2 flex flex-wrap gap-2">
                <span
                  class="rounded-full px-3 py-1 text-xs font-medium"
                  :style="getClientTypeBadgeStyle(selectedClientDetails.client_type_id)"
                >
                  {{ getClientTypeLabel(selectedClientDetails.client_type) }}
                </span>
                <span
                  v-if="selectedClientDetails.deleted_at"
                  class="rounded-full bg-rose-100 px-3 py-1 text-xs font-medium text-rose-700"
                >
                  Удалён
                </span>
              </div>
            </div>
          </div>
          <div class="grid shrink-0 grid-cols-2 gap-2 text-right">
            <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
              <p class="text-[11px] uppercase tracking-wide text-slate-500">Покупки</p>
              <p class="text-sm font-semibold text-slate-900">{{ formatCurrency(selectedClientDetails.total_price) }}</p>
            </div>
            <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
              <p class="text-[11px] uppercase tracking-wide text-slate-500">Заказов</p>
              <p class="text-sm font-semibold text-slate-900">{{ Number(selectedClientDetails.total_amount || 0) }}</p>
            </div>
            <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
              <p class="text-[11px] uppercase tracking-wide text-slate-500">Средний чек</p>
              <p class="text-sm font-semibold text-slate-900">{{ formatCurrency(selectedClientDetails.average_price) }}</p>
            </div>
            <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
              <p class="text-[11px] uppercase tracking-wide text-slate-500">Бонусы</p>
              <p class="text-sm font-semibold text-emerald-700">{{ formatCurrency(selectedClientDetails.bonuses) }}</p>
            </div>
          </div>
        </div>
      </section>

      <section class="flex flex-wrap gap-2">
        <Button
          label="Редактирование"
          icon="pi pi-pencil"
          size="small"
          :severity="clientDetailsTab === 'edit' ? 'success' : 'secondary'"
          :outlined="clientDetailsTab !== 'edit'"
          @click="clientDetailsTab = 'edit'"
        />
        <Button
          label="Напоминания"
          icon="pi pi-calendar"
          size="small"
          :severity="clientDetailsTab === 'reminders' ? 'success' : 'secondary'"
          :outlined="clientDetailsTab !== 'reminders'"
          @click="clientDetailsTab = 'reminders'"
        />
        <Button
          label="История закупок"
          icon="pi pi-shopping-bag"
          size="small"
          :severity="clientDetailsTab === 'sales' ? 'success' : 'secondary'"
          :outlined="clientDetailsTab !== 'sales'"
          @click="clientDetailsTab = 'sales'"
        />
      </section>

      <section v-if="clientDetailsTab === 'edit'" class="rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <p class="mb-4 text-sm font-semibold text-slate-900">Редактирование клиента</p>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FloatLabel variant="on">
            <InputText id="detail-client-name" v-model.trim="selectedClientDetails.name" class="w-full" />
            <label for="detail-client-name">Имя</label>
          </FloatLabel>

          <FloatLabel variant="on">
            <InputText id="detail-client-phone" :modelValue="formatPhone(selectedClientDetails.phone)" class="w-full" disabled />
            <label for="detail-client-phone">Телефон</label>
          </FloatLabel>

          <FloatLabel variant="on">
            <InputText id="detail-client-email" v-model.trim="selectedClientDetails.email" class="w-full" />
            <label for="detail-client-email">Email</label>
          </FloatLabel>

          <FloatLabel variant="on">
            <Select
              id="detail-client-sex"
              v-model="selectedClientDetails.sex_id"
              :options="sex"
              optionLabel="name"
              optionValue="id"
              showClear
              class="w-full"
            />
            <label for="detail-client-sex">Пол</label>
          </FloatLabel>

          <FloatLabel variant="on" class="md:col-span-2">
            <Textarea
              id="detail-client-description"
              v-model.trim="selectedClientDetails.description"
              rows="3"
              class="w-full"
            />
            <label for="detail-client-description">Описание</label>
          </FloatLabel>

          <FloatLabel variant="on">
            <Select
              id="detail-client-type"
              v-model="selectedClientDetails.client_type_id"
              :options="clientTypes"
              optionLabel="name"
              optionValue="id"
              showClear
              class="w-full"
            />
            <label for="detail-client-type">Тип клиента</label>
          </FloatLabel>

          <FloatLabel variant="on">
            <InputNumber
              id="detail-client-bonuses"
              v-model="selectedClientDetails.bonuses"
              :min="0"
              mode="decimal"
              :minFractionDigits="0"
              :maxFractionDigits="0"
              class="w-full"
            />
            <label for="detail-client-bonuses">Бонусы</label>
          </FloatLabel>
        </div>

      </section>

      <section v-if="clientDetailsTab === 'reminders'" class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div class="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <p class="text-sm font-semibold text-slate-900">Напоминания</p>
            <p class="mt-1 text-xs text-slate-500">Звонки, сообщения и повторные контакты по клиенту</p>
          </div>
          <Button
            label="Все напоминания"
            icon="pi pi-calendar"
            severity="secondary"
            outlined
            size="small"
            @click="goToClientReminders"
          />
        </div>

        <div class="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
          <div class="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p class="mb-4 text-sm font-semibold text-slate-900">Добавить напоминание</p>

            <div class="space-y-4">
              <FloatLabel variant="on">
                <InputText id="client-reminder-title" v-model.trim="clientReminderForm.title" class="w-full" />
                <label for="client-reminder-title">Название</label>
              </FloatLabel>

              <FloatLabel variant="on">
                <Select
                  id="client-reminder-type"
                  v-model="clientReminderForm.type"
                  :options="clientReminderTypeOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="w-full"
                />
                <label for="client-reminder-type">Тип</label>
              </FloatLabel>

              <FloatLabel variant="on">
                <InputText id="client-reminder-due-at" v-model="clientReminderForm.due_at" type="datetime-local" class="w-full" />
                <label for="client-reminder-due-at">Дата и время</label>
              </FloatLabel>

              <FloatLabel variant="on">
                <Textarea
                  id="client-reminder-description"
                  v-model.trim="clientReminderForm.description"
                  rows="3"
                  class="w-full"
                />
                <label for="client-reminder-description">Комментарий</label>
              </FloatLabel>

              <Button
                label="Сохранить напоминание"
                icon="pi pi-check"
                severity="success"
                class="w-full"
                :loading="clientRemindersSaving"
                @click="createClientReminder"
              />
            </div>
          </div>

          <div>
            <div v-if="clientRemindersLoading" class="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
              Загрузка напоминаний...
            </div>

            <div v-else-if="!clientReminders.length" class="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
              У клиента пока нет напоминаний
            </div>

            <div v-else class="space-y-3">
              <article
                v-for="reminder in clientReminders"
                :key="reminder.id"
                class="rounded-2xl border px-4 py-3 shadow-sm"
                :class="getReminderCardClass(reminder)"
              >
                <div class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div class="min-w-0">
                    <div class="flex flex-wrap items-center gap-2">
                      <p class="text-sm font-semibold text-slate-900">{{ reminder.title }}</p>
                      <span class="rounded-full px-2.5 py-1 text-[11px] font-medium" :class="getReminderTypeClass(reminder.type)">
                        {{ getReminderTypeLabel(reminder.type) }}
                      </span>
                      <span class="rounded-full px-2.5 py-1 text-[11px] font-medium" :class="getReminderStatusClass(reminder.status)">
                        {{ getReminderStatusLabel(reminder.status) }}
                      </span>
                    </div>
                    <p v-if="reminder.description" class="mt-2 text-sm text-slate-500">{{ reminder.description }}</p>
                  </div>

                  <div class="shrink-0 text-left md:text-right">
                    <p class="text-sm font-medium text-slate-900">{{ formatDateTime(reminder.due_at) }}</p>
                    <p class="mt-1 text-xs" :class="reminder.is_overdue ? 'text-rose-600' : 'text-slate-500'">
                      {{ getReminderDueLabel(reminder) }}
                    </p>
                    <div class="mt-3 flex flex-wrap gap-2 md:justify-end">
                      <Button
                        v-if="reminder.status === 'pending'"
                        label="Выполнено"
                        icon="pi pi-check"
                        severity="success"
                        outlined
                        size="small"
                        @click="completeClientReminder(reminder)"
                      />
                      <Button
                        label="Удалить"
                        icon="pi pi-trash"
                        severity="danger"
                        outlined
                        size="small"
                        @click="deleteClientReminder(reminder)"
                      />
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section v-if="clientDetailsTab === 'sales'" class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div class="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <p class="text-sm font-semibold text-slate-900">История покупок</p>
            <p class="mt-1 text-xs text-slate-500">Заказы клиента из таблицы продаж</p>
          </div>
          <div class="flex flex-wrap gap-2">
            <div class="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-right">
              <p class="text-[11px] uppercase tracking-wide text-slate-500">Чеков</p>
              <p class="text-sm font-semibold text-slate-900">{{ clientSales.length }}</p>
            </div>
            <div class="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-right">
              <p class="text-[11px] uppercase tracking-wide text-slate-500">Сумма покупок</p>
              <p class="text-sm font-semibold text-slate-900">{{ formatCurrency(clientSalesTotalAmount) }}</p>
            </div>
          </div>
        </div>

        <div v-if="clientSalesLoading" class="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
          Загрузка покупок клиента...
        </div>

        <div v-else-if="!clientSales.length" class="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
          У клиента пока нет покупок
        </div>

        <div v-else class="overflow-auto rounded-2xl border border-slate-200">
          <table class="min-w-full divide-y divide-slate-200 text-sm">
            <thead class="bg-slate-50">
              <tr>
                <th class="px-4 py-3 text-left font-semibold text-slate-600">Номер</th>
                <th class="px-4 py-3 text-left font-semibold text-slate-600">Дата</th>
                <th class="px-4 py-3 text-left font-semibold text-slate-600">Продавец</th>
                <th class="px-4 py-3 text-left font-semibold text-slate-600">Счёт</th>
                <th class="px-4 py-3 text-left font-semibold text-slate-600">Товаров</th>
                <th class="px-4 py-3 text-left font-semibold text-slate-600">К оплате</th>
                <th class="px-4 py-3 text-right font-semibold text-slate-600">Действие</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 bg-white">
              <tr
                v-for="sale in clientSales"
                :key="sale.id"
                class="transition-colors hover:bg-slate-50"
              >
                <td class="px-4 py-3 font-medium text-slate-900">{{ sale.sale_id }}</td>
                <td class="px-4 py-3 text-slate-600">{{ formatDateTime(sale.date) }}</td>
                <td class="px-4 py-3 text-slate-600">{{ sale.employee || '—' }}</td>
                <td class="px-4 py-3 text-slate-600">{{ formatSaleAccountLabel(sale) }}</td>
                <td class="px-4 py-3 text-slate-600">{{ Number(sale.items_quantity || 0) }}</td>
                <td class="px-4 py-3 font-semibold text-slate-900">{{ formatCurrency(sale.price_after_discount) }}</td>
                <td class="px-4 py-3 text-right">
                  <Button
                    label="Подробнее"
                    icon="pi pi-eye"
                    severity="secondary"
                    outlined
                    size="small"
                    @click="openSaleDetails(sale)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
    <template #footer>
      <div class="flex w-full flex-wrap justify-between gap-2">
        <div class="flex flex-1 justify-center gap-2 w-full">
          <Button
            v-if="!selectedClientDetails?.deleted_at"
            class="w-1/2"
            label="Сохранить"
            severity="success"
            icon="pi pi-check"
            :loading="detailsSaving"
            @click="saveClientDetails"
          />
          <Button
            v-if="selectedClientDetails?.deleted_at"
            class="w-1/2"
            label="Восстановить"
            severity="success"
            icon="pi pi-refresh"
            @click="restoreClient"
          />
          <Button
            v-else
            class="w-1/2"
            label="Удалить"
            severity="danger"
            icon="pi pi-trash"
            @click="deleteClient"
          />
        <!-- <Button label="Закрыть" severity="secondary" outlined @click="showDetailsDialog = false" fluid /> -->
        </div>
      </div>
    </template>
  </Dialog>

  <Dialog
    v-model:visible="showSaleDetailsDialog"
    modal
    header="Детали продажи"
    :style="{ width: '1040px', maxWidth: '96vw' }"
    @hide="handleSaleDetailsHide"
  >
    <div v-if="saleDetailsLoading" class="py-6 text-base text-slate-500">Загрузка деталей...</div>
    <div v-else-if="!selectedSaleDetails" class="py-6 text-base text-slate-500">Данные по продаже не найдены</div>

    <div v-else class="grid gap-4 lg:grid-cols-[18rem_minmax(0,1fr)]">
      <section class="rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <h3 class="mb-3 text-lg font-semibold text-slate-900">Продажа</h3>
        <div class="space-y-3 text-sm">
          <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p class="text-xs uppercase tracking-wide text-slate-500">Номер</p>
            <p class="mt-1 font-semibold text-slate-900">{{ selectedSaleDetails.sale_id }}</p>
          </div>
          <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p class="text-xs uppercase tracking-wide text-slate-500">Дата</p>
            <p class="mt-1 font-semibold text-slate-900">{{ formatDateTime(selectedSaleDetails.date) }}</p>
          </div>
          <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p class="text-xs uppercase tracking-wide text-slate-500">Клиент</p>
            <p class="mt-1 font-semibold text-slate-900">{{ selectedSaleDetails.client?.name || selectedClientDetails?.name || 'Без клиента' }}</p>
            <p class="mt-1 text-slate-500">{{ formatPhone(selectedSaleDetails.client?.phone || selectedClientDetails?.phone) || 'Телефон не указан' }}</p>
          </div>
          <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p class="text-xs uppercase tracking-wide text-slate-500">Продавец</p>
            <p class="mt-1 font-semibold text-slate-900">{{ selectedSaleDetails.employee?.name || '—' }}</p>
          </div>
          <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p class="text-xs uppercase tracking-wide text-slate-500">Счёт</p>
            <p class="mt-1 font-semibold text-slate-900">{{ selectedSaleDetails.bank }} · {{ selectedSaleDetails.account }}</p>
          </div>
        </div>
      </section>

      <section class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div class="mx-auto w-full max-w-3xl rounded-2xl border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-4 md:p-5">
          <div class="border-b border-dashed border-slate-300 pb-4 text-center">
            <p class="text-xs font-medium uppercase tracking-[0.22em] text-slate-500">Чек продажи</p>
            <p class="mt-2 text-2xl font-semibold text-slate-900">{{ selectedSaleDetails.sale_id }}</p>
            <p class="mt-1 text-sm text-slate-500">{{ formatDateTime(selectedSaleDetails.date) }}</p>
          </div>

          <div class="mt-4 space-y-2 border-b border-dashed border-slate-300 pb-4 text-sm text-slate-600">
            <div class="flex items-start justify-between gap-3">
              <span>Клиент</span>
              <div class="text-right">
                <p class="font-medium text-slate-900">{{ selectedSaleDetails.client?.name || selectedClientDetails?.name || 'Без клиента' }}</p>
                <p class="text-xs text-slate-500">{{ formatPhone(selectedSaleDetails.client?.phone || selectedClientDetails?.phone) || 'Телефон не указан' }}</p>
              </div>
            </div>
            <div class="flex items-start justify-between gap-3">
              <span>Счёт</span>
              <span class="text-right font-medium text-slate-900">{{ selectedSaleDetails.bank }} · {{ selectedSaleDetails.account }}</span>
            </div>
            <div class="flex items-start justify-between gap-3">
              <span>Продавец</span>
              <span class="text-right font-medium text-slate-900">{{ selectedSaleDetails.employee?.name || '—' }}</span>
            </div>
          </div>

          <div class="mt-4 space-y-3">
            <article
              v-for="item in saleReceiptItems"
              :key="item.id"
              class="rounded-2xl border border-slate-200 bg-slate-50 p-3"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="truncate text-base font-semibold text-slate-900">{{ item.label }}</p>
                  <p class="mt-1 text-xs text-slate-500">{{ item.meta }}</p>
                </div>
                <div class="shrink-0 text-right">
                  <p class="text-base font-semibold text-slate-900">{{ formatCurrency(item.total_price) }}</p>
                  <p class="mt-1 text-xs text-slate-500">{{ item.quantityLabel }}</p>
                </div>
              </div>

              <div v-if="item.showComposition" class="mt-3">
                <button
                  type="button"
                  class="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
                  @click="toggleSaleItemDetails(item.id)"
                >
                  <span class="font-medium">
                    Состав позиции
                    <span class="text-slate-500">({{ item.components.length }})</span>
                  </span>
                  <i
                    class="pi text-xs text-slate-500"
                    :class="isSaleItemExpanded(item.id) ? 'pi-chevron-up' : 'pi-chevron-down'"
                  />
                </button>

                <div
                  v-if="isSaleItemExpanded(item.id)"
                  class="mt-2 space-y-2 rounded-xl border border-slate-200 bg-white p-3"
                >
                  <div
                    v-for="component in item.components"
                    :key="`${item.id}-${component.product_id}-${component.barcode}`"
                    class="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5"
                  >
                    <div class="flex items-start justify-between gap-3 text-sm">
                      <div class="min-w-0">
                        <p class="truncate font-medium text-slate-900">{{ component.name }}</p>
                        <p class="truncate text-xs text-slate-500">{{ component.barcode || 'Штрихкод не указан' }}</p>
                        <p class="mt-1 truncate text-xs text-slate-400">
                          {{ component.classification || 'Без класса' }} · {{ component.category || 'Без категории' }}
                        </p>
                      </div>
                      <div class="shrink-0 text-right text-xs text-slate-600">
                        <p>{{ Number(component.quantity || 0) }} × {{ formatCurrency(component.unit_price) }}</p>
                        <p class="mt-1 font-semibold text-slate-900">{{ formatCurrency(component.total_price) }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </div>

          <div class="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
            <div class="flex items-center justify-between gap-3 text-sm text-slate-700">
              <span>Сумма по чеку</span>
              <span class="font-medium text-slate-900">{{ formatCurrency(selectedSaleDetails.total_amount) }}</span>
            </div>
            <div v-if="saleDiscountAmount > 0" class="mt-2 flex items-center justify-between gap-3 text-sm text-slate-700">
              <span>Скидка / бонусы</span>
              <span class="font-medium text-emerald-700">-{{ formatCurrency(saleDiscountAmount) }}</span>
            </div>
            <div class="mt-3 flex items-center justify-between gap-3 border-t border-dashed border-emerald-200 pt-3">
              <span class="text-base font-semibold text-slate-900">К оплате</span>
              <span class="text-2xl font-semibold text-slate-900">{{ formatCurrency(selectedSaleDetails.price_after_discount) }}</span>
            </div>
          </div>
        </div>
      </section>
    </div>

    <template #footer>
      <Button label="Закрыть" severity="secondary" outlined @click="showSaleDetailsDialog = false" />
    </template>
  </Dialog>
</template>

<script setup>
import { useRouter } from 'vue-router'
import { computed, onMounted, ref } from 'vue'
import axios from '../lib/axios'
import * as XLSX from 'xlsx'
import { useToast } from 'primevue/usetoast'
import Toast from 'primevue/toast'

const toast = useToast()
const router = useRouter()

const clients = ref([])
const searchQuery = ref('')
const viewMode = ref('table')
const addClientDialog = ref(false)
const showDetailsDialog = ref(false)
const clientDetailsTab = ref('edit')
const selectedClientDetails = ref(null)
const detailsSaving = ref(false)
const clientSales = ref([])
const clientSalesLoading = ref(false)
const showSaleDetailsDialog = ref(false)
const saleDetailsLoading = ref(false)
const selectedSaleDetails = ref(null)
const expandedSaleItemIds = ref([])
const clientReminders = ref([])
const clientRemindersLoading = ref(false)
const clientRemindersSaving = ref(false)
const sex = ref([])
const clientTypes = ref([])
const selectedClientTypeFilter = ref('all')
const createdAtFilter = ref({
  from: '',
  to: '',
})
const clientScope = ref('active')
const sortField = ref('created_at')
const sortOrder = ref('desc')
const importFileInput = ref(null)
const importInProgress = ref(false)
const SINGLE_PRODUCT_ORDER_TYPE_ID = 8
const EXTRA_WORK_ORDER_TYPE_ID = 9
let clientSalesRequestId = 0

const clientReminderTypeOptions = [
  { label: 'Звонок', value: 'call' },
  { label: 'Сообщение', value: 'message' },
  { label: 'День рождения', value: 'birthday' },
  { label: 'Повторный контакт', value: 'follow_up' },
  { label: 'Другое', value: 'custom' },
]

const clientTypeFilterOptions = computed(() => ([
  { label: 'Все типы клиентов', value: 'all' },
  ...clientTypes.value.map((item) => ({
    label: item.name,
    value: Number(item.id),
  })),
]))

const client = ref({
  name: '',
  phone: null,
  email: '',
  description: '',
  client_type_id: null,
  sex: null,
  sex_id: null,
})

const clientReminderForm = ref({
  title: '',
  type: 'call',
  due_at: '',
  description: '',
})

const filteredClients = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()
  const filtered = clients.value.filter((item) => {
    const matchesType = selectedClientTypeFilter.value === 'all'
      || Number(item.client_type_id) === Number(selectedClientTypeFilter.value)
    const matchesCreatedAt = matchesClientCreatedAt(
      item.created_at,
      createdAtFilter.value.from,
      createdAtFilter.value.to,
    )

    if (!matchesType || !matchesCreatedAt) {
      return false
    }

    if (!q) {
      return true
    }

    const haystack = [
      item.name,
      item.phone,
      item.email,
      item.description,
      item.client_type,
      item.sex,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()

    return haystack.includes(q)
  })

  return filtered.sort((a, b) => compareClients(a, b, sortField.value, sortOrder.value))
})

const formattedClientCount = computed(() => {
  return Number(filteredClients.value.length || 0).toLocaleString('ru-RU')
})

const clientSalesTotalAmount = computed(() => {
  return clientSales.value.reduce((sum, sale) => sum + Number(sale.price_after_discount || 0), 0)
})

const saleReceiptItems = computed(() => {
  const items = selectedSaleDetails.value?.items || []

  return items.map((item, index) => {
    const components = Array.isArray(item.components) ? item.components : []
    const isSingleProduct = Number(item.order_type_id || 0) === SINGLE_PRODUCT_ORDER_TYPE_ID
    const componentsTotal = components.reduce((sum, component) => sum + Number(component.total_price || 0), 0)
    const totalPrice = Number(item.total_price || 0)
    const extraCharge = Math.max(0, totalPrice - componentsTotal)
    const componentQuantity = components.reduce((sum, component) => sum + Number(component.quantity || 0), 0)
    const displayQuantity = isSingleProduct && componentQuantity > 0
      ? componentQuantity
      : Number(item.quantity || 0) || 1
    const primaryName = item.title
      ? String(item.title).trim()
      : Number(item.order_type_id || 0) === EXTRA_WORK_ORDER_TYPE_ID
        ? 'Доп работа'
        : components.length === 1
          ? components[0]?.name
          : item.product_type || null

    return {
      ...item,
      label: primaryName || `Позиция ${index + 1}`,
      showComposition: !isSingleProduct && components.length > 0,
      meta: [
        item.product_type,
        item.product_level,
        components.length > 1 ? `${components.length} компонентов` : null,
      ].filter(Boolean).join(' · '),
      quantityLabel: `Кол-во: ${displayQuantity}`,
      components,
      componentsTotal,
      extraCharge,
      extraChargeLabel: components.length ? 'Сборка / наценка' : 'Доп. работа',
    }
  })
})

const saleDiscountAmount = computed(() => {
  const total = Number(selectedSaleDetails.value?.total_amount || 0)
  const payable = Number(selectedSaleDetails.value?.price_after_discount || 0)

  return Math.max(0, total - payable)
})

const formatCurrency = (value) => {
  return `${Number(value || 0).toLocaleString('ru-RU')} ₸`
}

const formatDate = (value) => {
  if (!value) return '—'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'

  return date.toLocaleDateString('ru-RU')
}

const formatDateTime = (value) => {
  if (!value) return '—'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)

  return date.toLocaleString('ru-RU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const getReminderTypeLabel = (type) => ({
  call: 'Звонок',
  message: 'Сообщение',
  birthday: 'День рождения',
  follow_up: 'Повторный контакт',
  custom: 'Другое',
}[type] || 'Другое')

const getReminderStatusLabel = (status) => ({
  pending: 'Ожидает',
  done: 'Выполнено',
  cancelled: 'Отменено',
}[status] || 'Ожидает')

const getReminderTypeClass = (type) => ({
  call: 'bg-blue-100 text-blue-700',
  message: 'bg-violet-100 text-violet-700',
  birthday: 'bg-pink-100 text-pink-700',
  follow_up: 'bg-amber-100 text-amber-700',
  custom: 'bg-slate-100 text-slate-700',
}[type] || 'bg-slate-100 text-slate-700')

const getReminderStatusClass = (status) => ({
  pending: 'bg-amber-100 text-amber-700',
  done: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-slate-100 text-slate-600',
}[status] || 'bg-slate-100 text-slate-600')

const getReminderCardClass = (reminder) => {
  if (reminder.status === 'done') {
    return 'border-emerald-200 bg-emerald-50/50'
  }

  if (reminder.is_overdue) {
    return 'border-rose-200 bg-rose-50/50'
  }

  return 'border-slate-200 bg-slate-50/70'
}

const getReminderDueLabel = (reminder) => {
  if (reminder.status === 'done') {
    return 'Напоминание выполнено'
  }

  if (reminder.is_overdue) {
    return reminder.days_left === null ? 'Просрочено' : `Просрочено на ${Math.abs(Number(reminder.days_left || 0))} дн.`
  }

  if (Number(reminder.days_left || 0) === 0) {
    return 'На сегодня'
  }

  return `Через ${Number(reminder.days_left || 0)} дн.`
}

const resetClientReminderForm = () => {
  const now = new Date()
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset())

  clientReminderForm.value = {
    title: '',
    type: 'call',
    due_at: now.toISOString().slice(0, 16),
    description: '',
  }
}

const toDateOnlyTimestamp = (value) => {
  const date = value instanceof Date ? new Date(value) : new Date(value)

  if (Number.isNaN(date.getTime())) {
    return null
  }

  date.setHours(0, 0, 0, 0)
  return date.getTime()
}

const matchesClientCreatedAt = (createdAt, from, to) => {
  if (!from && !to) {
    return true
  }

  const createdAtTime = toDateOnlyTimestamp(createdAt)

  if (createdAtTime === null) {
    return false
  }

  const fromTime = from ? toDateOnlyTimestamp(from) : null
  const toTime = to ? toDateOnlyTimestamp(to) : fromTime

  if (fromTime === null && toTime === null) {
    return true
  }

  if (fromTime !== null && toTime !== null) {
    return createdAtTime >= fromTime && createdAtTime <= toTime
  }

  return createdAtTime === fromTime
}

const getSortValue = (clientItem, field) => {
  if (field === 'created_at') {
    return clientItem.created_at ? new Date(clientItem.created_at).getTime() : 0
  }

  return Number(clientItem[field] || 0)
}

const compareClients = (left, right, field, order) => {
  const leftValue = getSortValue(left, field)
  const rightValue = getSortValue(right, field)

  if (leftValue === rightValue) {
    return String(left.name || '').localeCompare(String(right.name || ''), 'ru')
  }

  return order === 'asc' ? leftValue - rightValue : rightValue - leftValue
}

const toggleSort = (field) => {
  if (sortField.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
    return
  }

  sortField.value = field
  sortOrder.value = 'desc'
}

const getSortIconClass = (field) => {
  if (sortField.value !== field) {
    return 'pi pi-sort-alt text-slate-300'
  }

  return sortOrder.value === 'asc'
    ? 'pi pi-sort-up text-emerald-600'
    : 'pi pi-sort-down text-emerald-600'
}

const getDefaultClientTypeId = () => {
  return clientTypes.value.find((item) => Number(item.id) === 1)?.id
    ?? clientTypes.value[0]?.id
    ?? null
}

const getClientTypeLabel = (value) => {
  return String(value || 'Обычный').trim()
}

const defaultClientTypeColor = '#10B981'

const normalizeClientTypeColor = (value) => {
  const normalized = String(value || '').trim().toUpperCase()
  return /^#[0-9A-F]{6}$/.test(normalized) ? normalized : defaultClientTypeColor
}

const hexToRgb = (hex) => {
  const normalized = normalizeClientTypeColor(hex).replace('#', '')
  const value = Number.parseInt(normalized, 16)

  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  }
}

const colorWithAlpha = (hex, alpha) => {
  const { r, g, b } = hexToRgb(hex)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const getClientTypeColor = (clientTypeId) => {
  const color = clientTypes.value.find((item) => Number(item.id) === Number(clientTypeId))?.color
  return normalizeClientTypeColor(color)
}

const getClientInitialStyle = (clientTypeId) => {
  const color = getClientTypeColor(clientTypeId)

  return {
    backgroundColor: colorWithAlpha(color, 0.16),
    color,
    boxShadow: `inset 0 0 0 1px ${colorWithAlpha(color, 0.18)}`,
  }
}

const getClientTypeBadgeStyle = (clientTypeId) => {
  const color = getClientTypeColor(clientTypeId)

  return {
    backgroundColor: colorWithAlpha(color, 0.14),
    color,
    boxShadow: `inset 0 0 0 1px ${colorWithAlpha(color, 0.18)}`,
  }
}

const getClientHeroStyle = (clientTypeId) => {
  const color = getClientTypeColor(clientTypeId)

  return {
    borderColor: colorWithAlpha(color, 0.22),
    background: `linear-gradient(135deg, ${colorWithAlpha(color, 0.16)} 0%, rgba(255, 255, 255, 1) 72%)`,
  }
}

const formatPhone = (value) => {
  const digits = String(value ?? '').replace(/\D+/g, '')
  if (!digits) return ''

  const normalized = digits.length === 10 ? `7${digits}` : digits
  if (normalized.length !== 11 || !normalized.startsWith('7')) {
    return String(value ?? '')
  }

  return `+${normalized[0]}(${normalized.slice(1, 4)})${normalized.slice(4, 7)}-${normalized.slice(7, 9)}-${normalized.slice(9, 11)}`
}

const initialFromName = (name) => {
  const source = String(name || '').trim()
  return source ? source.charAt(0).toUpperCase() : '?'
}

const formatSaleAccountLabel = (sale) => {
  const bank = String(sale?.account_name || '').trim()
  const account = String(sale?.account || '').trim()

  if (bank && account) {
    return `${bank} · ${account}`
  }

  return bank || account || '—'
}

const isSaleItemExpanded = (itemId) => expandedSaleItemIds.value.includes(itemId)

const toggleSaleItemDetails = (itemId) => {
  if (isSaleItemExpanded(itemId)) {
    expandedSaleItemIds.value = expandedSaleItemIds.value.filter((id) => id !== itemId)
    return
  }

  expandedSaleItemIds.value = [...expandedSaleItemIds.value, itemId]
}

const fetchClientSales = async (clientId) => {
  if (!clientId) {
    clientSales.value = []
    return
  }

  const requestId = ++clientSalesRequestId
  clientSalesLoading.value = true

  try {
    const { data } = await axios.get('/api/sale', { params: { client_id: clientId } })

    if (requestId !== clientSalesRequestId) {
      return
    }

    clientSales.value = Array.isArray(data) ? data : []
  } catch {
    if (requestId !== clientSalesRequestId) {
      return
    }

    clientSales.value = []
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить покупки клиента',
      life: 4000,
    })
  } finally {
    if (requestId === clientSalesRequestId) {
      clientSalesLoading.value = false
    }
  }
}

const fetchClientReminders = async (clientId) => {
  if (!clientId) {
    clientReminders.value = []
    return
  }

  clientRemindersLoading.value = true

  try {
    const { data } = await axios.get('/api/client-reminders', {
      params: {
        client_id: clientId,
        scope: 'all',
        limit: 100,
      },
    })
    clientReminders.value = Array.isArray(data) ? data : []
  } catch (error) {
    clientReminders.value = []
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: extractErrorMessage(error, 'Не удалось загрузить напоминания клиента'),
      life: 4000,
    })
  } finally {
    clientRemindersLoading.value = false
  }
}

const openSaleDetails = async (sale) => {
  if (!sale?.id) {
    return
  }

  showSaleDetailsDialog.value = true
  saleDetailsLoading.value = true
  selectedSaleDetails.value = null
  expandedSaleItemIds.value = []

  try {
    const { data } = await axios.get(`/api/sale-items/${sale.id}`)
    selectedSaleDetails.value = data || null
    expandedSaleItemIds.value = (selectedSaleDetails.value?.items || [])
      .filter((item) => Number(item.order_type_id || 0) !== 8 && Array.isArray(item.components) && item.components.length > 0)
      .slice(0, 1)
      .map((item) => item.id)
  } catch {
    selectedSaleDetails.value = null
    expandedSaleItemIds.value = []
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить детали продажи',
      life: 4000,
    })
  } finally {
    saleDetailsLoading.value = false
  }
}

const handleSaleDetailsHide = () => {
  selectedSaleDetails.value = null
  saleDetailsLoading.value = false
  expandedSaleItemIds.value = []
}

const openDetails = (clientItem) => {
  clientDetailsTab.value = 'edit'
  selectedClientDetails.value = {
    ...clientItem,
    client_type_id: clientItem.client_type_id ?? getDefaultClientTypeId(),
    sex_id: clientItem.sex_id ?? (sex.value.find((item) => item.name === clientItem.sex)?.id ?? null),
  }
  clientSales.value = []
  clientReminders.value = []
  resetClientReminderForm()
  showDetailsDialog.value = true
  showSaleDetailsDialog.value = false
  selectedSaleDetails.value = null
  expandedSaleItemIds.value = []
  void fetchClientSales(clientItem.id)
  void fetchClientReminders(clientItem.id)
}

const handleClientDetailsHide = () => {
  clientDetailsTab.value = 'edit'
  clientSalesRequestId++
  clientSales.value = []
  clientSalesLoading.value = false
  clientReminders.value = []
  clientRemindersLoading.value = false
  selectedClientDetails.value = null
  showSaleDetailsDialog.value = false
  handleSaleDetailsHide()
}

const setClientScope = async (scope) => {
  if (clientScope.value === scope) {
    return
  }

  clientScope.value = scope
  showDetailsDialog.value = false
  showSaleDetailsDialog.value = false
  selectedClientDetails.value = null
  selectedSaleDetails.value = null
  clientSales.value = []
  clientReminders.value = []
  await fetchClients()
}

const createClientReminder = async () => {
  if (!selectedClientDetails.value?.id) {
    return
  }

  clientRemindersSaving.value = true

  try {
    await axios.post('/api/client-reminders', {
      client_id: selectedClientDetails.value.id,
      title: clientReminderForm.value.title,
      type: clientReminderForm.value.type,
      due_at: clientReminderForm.value.due_at,
      description: clientReminderForm.value.description,
    })

    toast.add({ severity: 'success', summary: 'Сохранено', detail: 'Напоминание добавлено', life: 3000 })
    resetClientReminderForm()
    await fetchClientReminders(selectedClientDetails.value.id)
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: extractErrorMessage(error, 'Не удалось добавить напоминание'),
      life: 4000,
    })
  } finally {
    clientRemindersSaving.value = false
  }
}

const completeClientReminder = async (reminder) => {
  try {
    await axios.post(`/api/client-reminders/${reminder.id}/complete`)
    toast.add({ severity: 'success', summary: 'Выполнено', detail: 'Напоминание закрыто', life: 3000 })
    await fetchClientReminders(selectedClientDetails.value?.id)
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: extractErrorMessage(error, 'Не удалось закрыть напоминание'),
      life: 4000,
    })
  }
}

const deleteClientReminder = async (reminder) => {
  try {
    await axios.delete(`/api/client-reminders/${reminder.id}`)
    toast.add({ severity: 'success', summary: 'Удалено', detail: 'Напоминание удалено', life: 3000 })
    await fetchClientReminders(selectedClientDetails.value?.id)
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: extractErrorMessage(error, 'Не удалось удалить напоминание'),
      life: 4000,
    })
  }
}

const goToClientReminders = () => {
  router.push({
    name: 'ClientReminders',
    query: selectedClientDetails.value?.id
      ? { client_id: String(selectedClientDetails.value.id) }
      : undefined,
  })
}

const saveClientDetails = async () => {
  if (!selectedClientDetails.value?.id) {
    return
  }

  detailsSaving.value = true

  try {
    const payload = {
      name: selectedClientDetails.value.name,
      email: selectedClientDetails.value.email || '',
      description: selectedClientDetails.value.description || '',
      client_type_id: selectedClientDetails.value.client_type_id || getDefaultClientTypeId(),
      sex_id: selectedClientDetails.value.sex_id,
      bonuses: Number(selectedClientDetails.value.bonuses || 0),
    }

    await axios.put(`/api/client/${selectedClientDetails.value.id}`, payload)
    toast.add({ severity: 'success', summary: 'Успешно', detail: 'Клиент обновлен', life: 3000 })
    await fetchClients()

    const updatedClient = clients.value.find((item) => item.id === selectedClientDetails.value.id)
    if (updatedClient) {
      selectedClientDetails.value = {
        ...updatedClient,
        client_type_id: updatedClient.client_type_id ?? getDefaultClientTypeId(),
        sex_id: updatedClient.sex_id ?? (sex.value.find((item) => item.name === updatedClient.sex)?.id ?? null),
      }
    }
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: extractErrorMessage(error, 'Не удалось обновить клиента'),
      life: 4000,
    })
  } finally {
    detailsSaving.value = false
  }
}

const deleteClient = async () => {
  if (!selectedClientDetails.value?.id) {
    return
  }

  try {
    await axios.delete(`/api/client/${selectedClientDetails.value.id}`)
    toast.add({ severity: 'success', summary: 'Успешно', detail: 'Клиент удален', life: 3000 })
    showDetailsDialog.value = false
    selectedClientDetails.value = null
    await fetchClients()
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: extractErrorMessage(error, 'Не удалось удалить клиента'),
      life: 4000,
    })
  }
}

const restoreClient = async () => {
  if (!selectedClientDetails.value?.id) {
    return
  }

  try {
    await axios.post(`/api/client/${selectedClientDetails.value.id}/restore`)
    toast.add({ severity: 'success', summary: 'Успешно', detail: 'Клиент восстановлен', life: 3000 })
    showDetailsDialog.value = false
    selectedClientDetails.value = null
    await fetchClients()
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: extractErrorMessage(error, 'Не удалось восстановить клиента'),
      life: 4000,
    })
  }
}

const fetchClients = async () => {
  const params = clientScope.value === 'deleted' ? { trashed: 'only' } : {}
  const { data } = await axios.get('/api/client', { params })
  clients.value = Array.isArray(data) ? data : []
}

const getSex = async () => {
  const res = await axios.get('/api/sex')
  sex.value = Array.isArray(res.data) ? res.data : []
}

const fetchClientTypes = async () => {
  const res = await axios.get('/api/client-types')
  clientTypes.value = Array.isArray(res.data) ? res.data : []

  if (!client.value.client_type_id) {
    client.value.client_type_id = getDefaultClientTypeId()
  }
}

const resetClientForm = () => {
  client.value = {
    name: '',
    phone: null,
    email: '',
    description: '',
    client_type_id: getDefaultClientTypeId(),
    sex: null,
    sex_id: null,
  }
}

const openImportPicker = () => {
  if (importInProgress.value) return
  importFileInput.value?.click()
}

const parseNumber = (value) => {
  if (value === null || value === undefined || value === '' || value === '-') {
    return 0
  }

  const normalized = String(value).replace(/\s+/g, '').replace(',', '.')
  const parsed = Number(normalized)

  return Number.isFinite(parsed) ? parsed : 0
}

const extractErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data
  const firstValidationError = responseData?.errors
    ? Object.values(responseData.errors).flat()[0]
    : null

  return firstValidationError || responseData?.message || fallbackMessage
}

const normalizeImportedClients = (rows) => {
  return rows
    .map((row) => ({
      phone: row.phone ?? null,
      name: String(row.name ?? '').trim(),
      email: row.email ?? null,
      description: String(row.description ?? '').trim(),
      client_type: String(row.client_type ?? row['Тип клиента'] ?? '').trim(),
      sex: String(row.sex ?? '').trim(),
      total_price: parseNumber(row.total_price),
      total_amount: parseNumber(row.total_amount),
      average_price: parseNumber(row.average_price),
      bonuses: parseNumber(row.bonuses),
    }))
    .filter((row) => row.name || row.phone || row.sex)
}

const exportClients = () => {
  if (!filteredClients.value.length) {
    toast.add({
      severity: 'warn',
      summary: 'Нет данных',
      detail: 'Нет клиентов для экспорта',
      life: 3000,
    })
    return
  }

  const rows = filteredClients.value.map((item) => ({
    'ФИО': item.name || '',
    'Телефон': formatPhone(item.phone) || '',
    'Email': item.email || '',
    'Описание': item.description || '',
    'Тип клиента': getClientTypeLabel(item.client_type),
    'Пол': item.sex || '',
    'Создан': formatDate(item.created_at),
    'Заказов': Number(item.total_amount || 0),
    'Покупки': Number(item.total_price || 0),
    'Средний чек': Number(item.average_price || 0),
    'Бонусы': Number(item.bonuses || 0),
    'Статус': item.deleted_at ? 'Удалён' : 'Активен',
  }))

  const worksheet = XLSX.utils.json_to_sheet(rows)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Clients')

  const scopeLabel = clientScope.value === 'deleted' ? 'deleted' : 'active'
  const dateLabel = new Date().toISOString().slice(0, 10)
  XLSX.writeFile(workbook, `clients-${scopeLabel}-${dateLabel}.xlsx`)
}

const addClient = async () => {
  try {
    client.value.sex_id = client.value.sex?.id || null
    const response = await axios.post('/api/client', client.value)
    if (response.status === 201) {
      toast.add({ severity: 'success', summary: 'Успешно', detail: 'Клиент добавлен', life: 3000 })
      addClientDialog.value = false
      resetClientForm()
      await fetchClients()
    }
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: extractErrorMessage(error, 'Не удалось добавить клиента'),
      life: 4000
    })
  }
}

const handleImportFile = async (event) => {
  const input = event?.target
  const [file] = input?.files || []

  if (!file) {
    return
  }

  importInProgress.value = true

  try {
    const buffer = await file.arrayBuffer()
    const workbook = XLSX.read(buffer, { type: 'array' })
    const firstSheetName = workbook.SheetNames[0]
    const worksheet = workbook.Sheets[firstSheetName]
    const rows = XLSX.utils.sheet_to_json(worksheet, {
      defval: null,
      raw: false,
    })

    const importedClients = normalizeImportedClients(rows)

    if (!importedClients.length) {
      throw new Error('В Excel нет строк для импорта.')
    }

    const { data } = await axios.post('/api/client/import', {
      clients: importedClients,
    })

    toast.add({
      severity: 'success',
      summary: 'Импорт завершен',
      detail: `Добавлено: ${data.imported ?? 0}, обновлено: ${data.updated ?? 0}`,
      life: 4000,
    })

    await fetchClients()
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка импорта',
      detail: extractErrorMessage(error, 'Не удалось импортировать клиентов'),
      life: 5000,
    })
  } finally {
    importInProgress.value = false
    if (input) {
      input.value = ''
    }
  }
}

onMounted(async () => {
  await Promise.all([fetchClients(), getSex(), fetchClientTypes()])
  resetClientForm()
  resetClientReminderForm()
})
</script>

<style scoped>
:deep(.p-inputtext),
:deep(.p-inputnumber-input),
:deep(.p-select-label),
:deep(.p-button-label) {
  font-size: 0.92rem;
}

@media (max-width: 1180px) {
  :deep(.p-inputtext),
  :deep(.p-inputnumber-input),
  :deep(.p-select-label),
  :deep(.p-button-label) {
    font-size: 0.85rem;
  }
}
</style>
